import { Buffer } from 'buffer'
import { Platform } from 'react-native'
import { createActorContext } from '@xstate/react'
import { assign, fromPromise, setup } from 'xstate'

import { BASE_URL } from '@/constants/config'
import { User } from '@/types/users'

// Metro does not polyfill Buffer on all platforms.
// Ensure a global reference exists before using Buffer APIs.
global.Buffer = global.Buffer || Buffer

interface CredentialStore {
  loadAuthAsync(): Promise<AuthState | null>
  saveAuthAsync(value: AuthState): Promise<void>
  updateAuthAsync(value: AuthState): Promise<void>
  clearAuthAsync(): Promise<void>
  broadcastLogoutAsync?(): Promise<void>
}

interface AuthState {
  user: User
  jwt: string
  refreshToken: string
  zdJwt: string
  lcToken: string
}

// Initialize a stub store, allowing an explicit replacement
// by default, this will do nothing but throw when loading
let store: any = {
  loadAuthAsync: async () => {
    throw new Error('stub storage has no values')
  },
  saveAuthAsync: async (_value: AuthState) => undefined,
  clearAuthAsync: async () => undefined
}

const signOutCallbacks: (() => void)[] = []

const authMachine = setup({
  actors: {
    loadStoredAuth: fromPromise(loadStoredAuth),
    refreshJWT: fromPromise(refreshJWT),
    logout: fromPromise(logout)
  },
  actions: {
    clearAuthData: assign(({ context, event }) => {
      store.clearAuthAsync()
      // Run all registered sign-out hooks (e.g., cache resets, token clears)
      for (const cb of signOutCallbacks) {
        try {
          cb()
        } catch (err) {
          console.log('signOutCallback error', err)
        }
      }
      return {
        values: undefined,
        error: undefined
      }
    }),
    broadcastSessionRevocation: () => {
      store.broadcastLogoutAsync?.().catch(() => undefined)
    }
  },
  types: {} as {
    context: {
      values: AuthState | null
      error: Error | null
    }
  }
}).createMachine({
  id: 'auth',
  initial: 'LOGGED_OUT',
  context: {
    values: null,
    error: null
  },
  states: {
    LOADING_STORED_AUTH: {
      invoke: {
        id: 'loadAuth',
        src: 'loadStoredAuth',
        onDone: {
          target: 'CHECK_AUTH',
          actions: assign({ values: ({ event }) => event.output })
        },
        onError: {
          target: 'LOGGED_OUT',
          actions: assign({ error: ({ event }) => event.error as Error })
        }
      }
    },
    CHECK_AUTH: {
      always: [
        {
          guard: ({ context }) => context.values === null,
          target: 'LOGGED_OUT'
        },
        {
          target: 'TOKEN_SIGNIN'
        }
      ]
    },
    TOKEN_SIGNIN: {
      invoke: {
        id: 'refreshAuth',
        src: 'refreshJWT',
        onDone: [
          {
            target: '#logged_in',
            actions: assign({ values: ({ event }) => event.output })
          }
        ],
        onError: {
          target: 'ERROR_LOGGING_OUT',
          actions: 'broadcastSessionRevocation'
        }
      }
    },
    LOGGED_IN: {
      id: 'logged_in',
      initial: 'READY',
      on: {
        LOGOUT: {
          target: 'LOGGING_OUT'
        },
        SESSION_REVOKED: {
          target: 'LOGGED_OUT',
          actions: 'clearAuthData'
        }
      },
      states: {
        READY: {
          on: {
            REFRESH: {
              target: 'REFRESHING'
            }
          }
        },
        REFRESHING: {
          invoke: {
            id: 'refreshAuth',
            src: 'refreshJWT',
            onDone: [
              {
                target: 'READY',
                actions: assign({ values: ({ event }) => event.output })
              }
            ],
            onError: {
              target: '#error_logging_out',
              actions: 'broadcastSessionRevocation'
            }
          }
        }
      }
    },
    ERROR_LOGGING_OUT: {
      id: 'error_logging_out',
      after: {
        // HACK for the state to be actually detected
        1: { target: 'LOGGING_OUT' }
      }
    },
    LOGGING_OUT: {
      invoke: {
        id: 'loggingOut',
        src: 'logout',
        onDone: [
          {
            target: 'LOGGED_OUT',
            actions: 'clearAuthData'
          }
        ],
        onError: {
          target: 'LOGGED_OUT',
          actions: 'clearAuthData'
        }
      }
    },
    LOGGED_OUT: {
      on: {
        SIGNUP_LOGIN: {
          target: '#logged_in',
          actions: assign(({ context, event }) => {
            store.saveAuthAsync(event.auth)
            return {
              ...context,
              values: event.auth
            }
          })
        },
        LOAD_AUTH: 'LOADING_STORED_AUTH'
      }
    }
  }
})

async function loadStoredAuth(): Promise<AuthState | null> {
  const authState = await store.loadAuthAsync()

  return authState && authState.refreshToken ? authState : null
}

async function refreshJWT() {
  for (let i = 0; i < 5; i++) {
    const controller = new AbortController()
    let timeout

    try {
      const auth = await store.loadAuthAsync()
      timeout = setTimeout(() => controller.abort(), 4000)
      const resp = await fetch(`${BASE_URL}/mobile/auth/refresh`, {
        signal: controller.signal,
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: auth?.user.id,
          token: auth?.refreshToken
        })
      })
      if (!resp.ok) {
        throw Error(`refresh failed: ${resp.status}`)
      }
      const authState: AuthState = await resp.json()
      authState.user.id = authState.user.id.toString()
      authState.refreshToken = auth.refreshToken
      await store.updateAuthAsync(authState)
      return authState
    } catch (e: any) {
      if (e.name === 'AbortError') {
        continue
      }
      throw Error(`refresh failed: ${e}`)
    } finally {
      clearTimeout(timeout)
    }
  }

  throw Error(`refresh failed: timed out`)
}

async function logout() {
  const auth = await store.loadAuthAsync()
  const resp = await fetch(`${BASE_URL}/mobile/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(auth?.jwt ? { Authorization: `Bearer ${auth.jwt}` } : {})
    },
    body: JSON.stringify({
      id: auth?.user.id,
      token: auth?.refreshToken
    })
  })
  await store.clearAuthAsync()
  await store.broadcastLogoutAsync?.()

  if (!resp.ok) {
    //we use a throw here explicitly to follow the "invoke machine" pattern of onError
    throw Error('logout failed')
  }
}

function toBase64(input: string) {
  if (Platform.OS === 'web') {
    return btoa(input)
  }
  return Buffer.from(input, 'utf-8').toString('base64')
}

export function createBasicAuth(login: string, pwd: string) {
  return 'Basic ' + toBase64(`${login}:${pwd}`)
}

export type AuthRequestError = Error & {
  errorCode?: number
  status?: number
  context?: string
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)
  return undefined
}

export function getAuthErrorCode(error: unknown): number | undefined {
  if (typeof error === 'number') return error
  if (!error || typeof error !== 'object') return undefined

  const authError = error as { errorCode?: unknown; error?: { code?: unknown }; code?: unknown }
  return asNumber(authError.errorCode) ?? asNumber(authError.error?.code) ?? asNumber(authError.code)
}

export function getAuthErrorStatus(error: unknown): number | undefined {
  if (typeof error === 'number') return error
  if (!error || typeof error !== 'object') return undefined

  return asNumber((error as { status?: unknown }).status)
}

export function getAuthErrorContext(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined

  const authError = error as { context?: unknown; error?: { context?: unknown } }
  const context = authError.context ?? authError.error?.context
  return typeof context === 'string' ? context : undefined
}

export async function throwAuthRequestError(resp: Response, fallback: string): Promise<never> {
  const payload = await resp.json().catch(() => undefined)
  const code = getAuthErrorCode(payload)
  const context =
    payload && typeof payload === 'object'
      ? ((payload as { error?: { context?: unknown }; context?: unknown }).error?.context ??
        (payload as { context?: unknown }).context)
      : undefined
  const error = new Error(code === 1007 ? 'blacklist - no access' : fallback) as AuthRequestError
  Object.assign(error, payload, { errorCode: code, status: resp.status, context })
  throw error
}

export async function loginWithPassword(countryCode: string, phone: string, password: string): Promise<AuthState> {
  const normalizedPhone = Number(phone).toString()
  const account = `${countryCode}${normalizedPhone}`
  const resp = await fetch(`${BASE_URL}/mobile/auth/login`, {
    method: 'POST',
    headers: {
      Authorization: createBasicAuth(account, password)
    },
    signal: AbortSignal.timeout(10000)
  })
  if (!resp.ok) {
    await throwAuthRequestError(resp, 'login failed')
  }

  const authState: AuthState = await resp.json()
  authState.user.id = authState.user.id.toString()
  return authState
}

function useCurrentJwt() {
  return AuthContext.useActorRef().getSnapshot().context.values?.jwt
}

function useCurrentZendeskJwt() {
  return AuthContext.useActorRef().getSnapshot().context.values?.zdJwt
}

function initStoreAndLoad(newStore: CredentialStore) {
  store = newStore
}

function setSignOutCallback(cb: () => void) {
  signOutCallbacks.push(cb)
}

export async function register(countryCode: string, phone: string, locale: string, newPassword: string): Promise<void> {
  const request = {
    countryCode,
    number: phone,
    locale
  }

  const basic = createBasicAuth(phone, newPassword)

  const resp = await fetch(`${BASE_URL}/mobile/auth/start-register`, {
    method: 'POST',
    headers: { Authorization: basic, 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  })
  if (!resp.ok) {
    await throwAuthRequestError(resp, 'register failed')
  }
}

export async function verifyToken(password: string, token: string, phone: string, countryCode: string): Promise<any> {
  const request = {
    countryCode,
    number: phone,
    token
  }
  const resp = await fetch(`${BASE_URL}/mobile/auth/finish-register`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Authorization: createBasicAuth(phone, password),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(request)
  })
  if (!resp.ok) {
    await throwAuthRequestError(resp, 'verify registration failed')
  }
  const authState = await resp.json()
  authState.user.id = authState.user.id.toString()
  return authState
}

const AuthContext = createActorContext(authMachine)

function AuthProvider({ children }: { children: React.ReactNode }) {
  return <AuthContext.Provider>{children}</AuthContext.Provider>
}

export {
  AuthContext,
  AuthProvider,
  useCurrentJwt,
  useCurrentZendeskJwt,
  initStoreAndLoad,
  setSignOutCallback,
  authMachine
}
export type { AuthState }
